import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react/react/docs/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`An `}<inlineCode parentName="p">{`ActionList`}</inlineCode>{` is a list of items which can be activated or selected. `}<inlineCode parentName="p">{`ActionList`}</inlineCode>{` is the base component for many of our menu-type components, including `}<inlineCode parentName="p">{`DropdownMenu`}</inlineCode>{` and `}<inlineCode parentName="p">{`ActionMenu`}</inlineCode>{`.`}</p>
    <h2>{`Deprecation`}</h2>
    <p>{`Use `}<a parentName="p" {...{
        "href": "/ActionList"
      }}>{`new version of ActionList`}</a>{` with composable API, design updates and accessibility fixes.`}</p>
    <h3>{`Before`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<ActionList
  items={[
    {text: 'New file'},
    {text: 'Copy link'},
    {text: 'Edit file'},
    ActionList.Divider,
    {text: 'Delete file', variant: 'danger'},
  ]}
/>
`}</code></pre>
    <h3>{`After`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<ActionList>
  <ActionList.Item>New file</ActionList.Item>
  <ActionList.Item>Copy link</ActionList.Item>
  <ActionList.Item>Edit file</ActionList.Item>
  <ActionList.Divider />
  <ActionList.Item variant="danger">Delete file</ActionList.Item>
</ActionList>
`}</code></pre>
    <p>{`Or continue using deprecated API:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`import {ActionList} from '@primer/react/deprecated'
`}</code></pre>
    <h2>{`Minimal example`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live deprecated",
        "live": true,
        "deprecated": true
      }}>{`<ActionList
  items={[
    {text: 'New file'},
    ActionList.Divider,
    {text: 'Copy link'},
    {text: 'Edit file'},
    {text: 'Delete file', variant: 'danger'},
  ]}
/>
`}</code></pre>
    <h2>{`Example with grouped items`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live deprecated",
        "live": true,
        "deprecated": true
      }}>{`<ActionList
  groupMetadata={[
    {groupId: '0'},
    {groupId: '1', header: {title: 'Live query', variant: 'subtle'}},
    {groupId: '2', header: {title: 'Layout', variant: 'subtle'}},
    {groupId: '3'},
    {groupId: '4'},
  ]}
  items={[
    {key: '1', leadingVisual: TypographyIcon, text: 'Rename', groupId: '0', trailingVisual: '⌘R'},
    {key: '2', leadingVisual: VersionsIcon, text: 'Duplicate', groupId: '0'},
    {key: '3', leadingVisual: SearchIcon, text: 'repo:github/github', groupId: '1'},
    {
      key: '4',
      leadingVisual: NoteIcon,
      text: 'Table',
      description: 'Information-dense table optimized for operations across teams',
      descriptionVariant: 'block',
      groupId: '2',
    },
    {
      key: '5',
      leadingVisual: ProjectIcon,
      text: 'Board',
      description: 'Kanban-style board focused on visual states',
      descriptionVariant: 'block',
      groupId: '2',
    },
    {
      key: '6',
      leadingVisual: FilterIcon,
      text: 'Save sort and filters to current view',
      disabled: true,
      groupId: '3',
    },
    {key: '7', leadingVisual: FilterIcon, text: 'Save sort and filters to new view', groupId: '3'},
    {key: '8', leadingVisual: GearIcon, text: 'View settings', groupId: '4', trailingVisual: ArrowRightIcon},
  ]}
/>
`}</code></pre>
    <h2>{`Example with custom item renderer`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "deprecated",
        "deprecated": true
      }}>{`<ActionList
  items={[
    {
      text: 'Vanilla link',
      renderItem: props => <ActionList.Item as="a" href="/about" {...props} />,
    },
    {
      text: 'React Router link',
      renderItem: props => <ActionList.Item as={ReactRouterLikeLink} to="/about" {...props} />,
    },
    {
      text: 'NextJS style',
      renderItem: props => (
        <NextJSLikeLink href="/about">
          <ActionList.Item as="a" {...props} />
        </NextJSLikeLink>
      ),
    },
  ]}
/>
`}</code></pre>
    <h2>{`Props`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": "center"
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`items`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`(ItemProps & Omit<React.ComponentPropsWithoutRef<'div'>, keyof ItemProps>) \\| ((Partial<ItemProps> & {renderItem: RenderItemFn}) & {key?: Key})`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><inlineCode parentName="td">{`undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Required. A list of item objects conforming to the `}<inlineCode parentName="td">{`ActionList.Item`}</inlineCode>{` props interface.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`renderItem`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`(props: ItemProps) => JSX.Element`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><inlineCode parentName="td">{`ActionList.Item`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Optional. If defined, each item in `}<inlineCode parentName="td">{`items`}</inlineCode>{` will be passed to this function, allowing for `}<inlineCode parentName="td">{`ActionList`}</inlineCode>{`-wide custom item rendering.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`groupMetadata`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`GroupProps[]`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><inlineCode parentName="td">{`undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Optional. If defined, `}<inlineCode parentName="td">{`ActionList`}</inlineCode>{` will group `}<inlineCode parentName="td">{`items`}</inlineCode>{` into `}<inlineCode parentName="td">{`ActionList.Group`}</inlineCode>{`s separated by `}<inlineCode parentName="td">{`ActionList.Divider`}</inlineCode>{` according to their `}<inlineCode parentName="td">{`groupId`}</inlineCode>{` property.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`showItemDividers`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "center"
          }}><inlineCode parentName="td">{`false`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Optional. If `}<inlineCode parentName="td">{`true`}</inlineCode>{` dividers will be displayed above each `}<inlineCode parentName="td">{`ActionList.Item`}</inlineCode>{` which does not follow an `}<inlineCode parentName="td">{`ActionList.Header`}</inlineCode>{` or `}<inlineCode parentName="td">{`ActionList.Divider`}</inlineCode></td>
        </tr>
      </tbody>
    </table>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      